import { ProductImage } from '@/core/components'
import type { ReactNode } from 'react'
import React, { useEffect, useState } from 'react'
import { useSuspenseFragment } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'
import { preloadImage } from '@/core/utils/preloadImage'
import { csx } from '@vtex/admin-ui'

import type { ProducSkuImageDesktopProps_ProductSku$key } from './__generated__/ProducSkuImageDesktopProps_ProductSku.graphql'
import { ProductSkuImagesGallery } from '../ProductSkuImagesGallery'
import { ProductSkuThumbnails } from './ProductSkuThumbnails'
import { useSelectedSkuLowResolutionImages } from '../hooks'
import { ProductSkuHighlightedPromotion } from '../../ProductSkuHighlightedPromotion'

const skuFragment = graphql`
  fragment ProducSkuImageDesktopProps_ProductSku on ProductSku {
    id
    name
    images {
      edges {
        node {
          id
          urlFullSize: url(size: 2000)
        }
      }
      ...ProductSkuImagesGallery_ProductSkuImageConnection
      ...ProductSkuThumbnails_ProductSkuImageConnection
    }
    ...ProductSkuHighlightedPromotion_ProductSku
  }
`

export type ProducSkuImageDesktopProps = {
  productSkuRef: ProducSkuImageDesktopProps_ProductSku$key
  children: ReactNode
}

export const ProducSkuImageDesktop = ({
  productSkuRef,
  children,
}: ProducSkuImageDesktopProps) => {
  const productSku = useSuspenseFragment(skuFragment, productSkuRef)
  const [shouldShowGallery, setShouldShowGallery] = useState(false)
  const [activeSkuImageIndex, setActiveSkuImageIndex] = useState(0)
  const [selectedSkuLowResolutionImage] = useSelectedSkuLowResolutionImages()

  const handleThumbnailSkuClick = (clickedSkuImageId: string) => {
    const index = productSku.images.edges.findIndex(
      (skuImage) => skuImage.node.id === clickedSkuImageId
    )

    setActiveSkuImageIndex(index)
  }

  const handleProductSkuImagesGalleryNavClick = (
    direction: 'prev' | 'next'
  ) => () => {
    setActiveSkuImageIndex((prev) =>
      direction === 'next'
        ? Math.min(prev + 1, productSku.images.edges.length - 1)
        : Math.max(prev - 1, 0)
    )
  }

  const activeSkuImage =
    productSku.images.edges[activeSkuImageIndex] ?? productSku.images.edges[0]

  useEffect(
    function preloadAllImages() {
      productSku.images.edges.map((skuImage) =>
        preloadImage(skuImage.node.urlFullSize)
      )
    },
    [productSku.images.edges]
  )

  return (
    <>
      <ProductSkuThumbnails
        activeSkuImage={activeSkuImage}
        onShowSkuImagesGalleryClick={() => setShouldShowGallery(true)}
        onThumbnailClick={handleThumbnailSkuClick}
        productSkuImageConnectionRef={productSku.images}
      />
      <div
        className={csx({
          position: 'relative',
        })}
      >
        <ProductSkuHighlightedPromotion productSkuRef={productSku} />
        <ProductImage
          src={activeSkuImage.node.urlFullSize}
          alt={productSku.name}
          lowResolutionSrc={selectedSkuLowResolutionImage}
          onClick={() => setShouldShowGallery(true)}
          className={csx({
            cursor: 'zoom-in',
          })}
        />
        {children}
      </div>
      {shouldShowGallery && (
        <ProductSkuImagesGallery
          activeSkuImage={activeSkuImage.node}
          productSkuImageConnectionRef={productSku.images}
          onCloseGallery={() => setShouldShowGallery(false)}
          onNextImageClick={handleProductSkuImagesGalleryNavClick('next')}
          onPreviousImageClick={handleProductSkuImagesGalleryNavClick('prev')}
        />
      )}
    </>
  )
}
